import React, { FunctionComponent, ReactElement } from 'react';
import TextField from '@material-ui/core/TextField';
import genericInputStyles from './GenericInputStyles';
import { IconButton, InputAdornment, Typography, Box, useTheme } from '@material-ui/core';
import { IRawError } from '../../model/IJsonSchema';
import DateRangeIcon from '@material-ui/icons/DateRange';
import { RenderInputParams } from '@material-ui/lab';
import { ICheckoutTheme } from '../../../common/theme/ICheckoutTheme';
import Translation from '../../../common/components/presentation/Translation/Translation';
import { GenericInputLabel } from './GenericInputLabel';
import clsx from 'clsx';

interface IGenericInputProps {
  label?: string;
  required: boolean;
  id: string;
  value: string;
  currentError?: IRawError | string;
  onChange?: (e: React.ChangeEvent<{ value: string }>) => void;
  className?: string;
  inputLabel?: boolean;
  type?: string;
  datePicker?: boolean;
  toggleDatePicker?: (value: boolean) => void;
  params?: RenderInputParams;
  tooltip?: string;
  placeholder?: string;
  phoneWithPrefix?: boolean;
  startAdornment?: ReactElement;
  classes?: {
    inputAdornmentIcon?: string;
    input?: string;
    containerInput?: string;
    helperTextError?: string;
  };
}

export const GenericInput: FunctionComponent<IGenericInputProps> = ({
  label,
  required,
  id,
  onChange,
  value,
  currentError,
  inputLabel,
  type,
  datePicker,
  toggleDatePicker,
  phoneWithPrefix,
  params,
  tooltip,
  placeholder,
  startAdornment,
  classes,
}) => {
  const styles = genericInputStyles();
  const { checkoutLite } = useTheme<ICheckoutTheme>();
  const showRequiredInPlaceholder = checkoutLite?.enquiryForm?.showRequiredInPlaceholder;
  const displayRequiredInPlaceholder =
    showRequiredInPlaceholder !== undefined ? showRequiredInPlaceholder && required : required;

  return (
    <div ref={params?.InputProps?.ref} className={clsx([styles.fieldWrapper, classes?.containerInput])}>
      {inputLabel !== false && <GenericInputLabel label={label} id={id} required={required} tooltip={tooltip} />}
      <TextField
        data-test={`text_input:${id}`}
        id={id}
        type={type}
        variant={'outlined'}
        className={styles.input}
        placeholder={placeholder ?? `${label}${displayRequiredInPlaceholder ? ' *' : ''}`}
        value={value ?? ''}
        error={!!currentError}
        onChange={onChange}
        helperText={
          currentError && (
            <Typography component={'span'} className={styles.errorText} data-test={`text_input:error:${id}`}>
              {currentError}
            </Typography>
          )
        }
        InputProps={{
          classes: {
            root: styles.root,
            focused: styles.focused,
            error: styles.inputError,
            notchedOutline: styles.notchedOutline,
            input: classes?.input,
          },
          endAdornment: (
            <InputAdornment position="end">
              {datePicker && (
                <IconButton onClick={() => toggleDatePicker(true)} component="span" className={styles.dateCalendar}>
                  <DateRangeIcon fontSize="small" />
                </IconButton>
              )}
            </InputAdornment>
          ),
          startAdornment: (
            <InputAdornment position="start">
              {phoneWithPrefix ? (
                <Box className={styles.phonePrefix}>
                  <Translation id={'enquiryForm.phonePrefix.label'} />
                </Box>
              ) : startAdornment ? (
                <IconButton className={classes?.inputAdornmentIcon} component="span">
                  {startAdornment}
                </IconButton>
              ) : (
                ''
              )}
            </InputAdornment>
          ),
        }}
        FormHelperTextProps={{
          classes: {
            root: clsx([styles.helperTextError, classes?.helperTextError]),
          },
        }}
        {...params?.inputProps}
      />
    </div>
  );
};

export default GenericInput;
